import clsx from 'clsx';
import {Fragment, useState} from 'react';
import {Link} from 'react-router-dom';
import {LandingPageContent} from './landing-page-content';
import {Navbar} from '@common/ui/navigation/navbar/navbar';
import {Button, ButtonProps} from '@common/ui/buttons/button';
import {Footer} from '@common/ui/footer/footer';
import {Trans} from '@common/i18n/trans';
import {AdHost} from '@common/admin/ads/ad-host';
import {createSvgIconFromTree} from '@common/icons/create-svg-icon';
import {MenuItemConfig} from '@common/core/settings/settings';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {useSettings} from '@common/core/settings/use-settings';
import {LandingPageNewLinkForm} from '@app/landing/landing-page-new-link-form';
import {useAuth} from '@common/auth/use-auth';
import {LandingPageStats} from '@app/landing/landing-page-stats';
import {PricingTable} from '@common/billing/pricing-table/pricing-table';
import {BillingCycleRadio} from '@common/billing/pricing-table/billing-cycle-radio';
import {UpsellBillingCycle} from '@common/billing/pricing-table/find-best-price';
import {useProducts} from '@common/billing/pricing-table/use-products';

interface ContentProps {
  content: LandingPageContent;
}

const productHighlights = [
  'Links curtos',
  'QR Codes',
  'Bio links',
  'Pixels',
  'Analytics',
  'Workspaces',
];

const platformMetrics = [
  {value: '8+', label: 'ferramentas em uma plataforma'},
  {value: '24/7', label: 'campanhas rastreáveis'},
  {value: '100%', label: 'controle por workspace'},
];

const workflowSteps = [
  {
    title: 'Crie',
    description: 'Cole a URL, configure alias, QR Code, bio link ou grupo.',
  },
  {
    title: 'Publique',
    description: 'Use domínio próprio, senha, expiração, pixel e regras.',
  },
  {
    title: 'Meça',
    description: 'Acompanhe cliques, origem, dispositivo e performance.',
  },
];

const useCases = [
  'Campanhas pagas',
  'Lançamentos',
  'Influenciadores',
  'Eventos',
  'Suporte e vendas',
  'Materiais impressos',
];

const planComparisonRows = [
  {
    feature: 'Links curtos',
    free: '10/mês',
    basic: '250/mês',
    premium: '2.500/mês',
  },
  {
    feature: 'QR Codes',
    free: '5/mês',
    basic: '100/mês',
    premium: '1.000/mês',
  },
  {
    feature: 'Cliques rastreados',
    free: '1.000/mês',
    basic: '25.000/mês',
    premium: '100.000/mês',
  },
  {
    feature: 'Biolinks',
    free: '1',
    basic: '3',
    premium: '10',
  },
  {
    feature: 'Domínios personalizados',
    free: 'Não incluso',
    basic: '1',
    premium: '5',
  },
  {
    feature: 'Pixels de rastreamento',
    free: 'Não incluso',
    basic: '3',
    premium: '20',
  },
  {
    feature: 'Workspaces',
    free: '1',
    basic: '1',
    premium: '3',
  },
  {
    feature: 'Membros da equipe',
    free: 'Não incluso',
    basic: 'Não incluso',
    premium: 'Até 5',
  },
  {
    feature: 'Marca no biolink',
    free: 'Shorty',
    basic: 'Shorty ou Credliber',
    premium: 'Pode ocultar',
  },
  {
    feature: 'API',
    free: 'Não incluso',
    basic: 'Não incluso',
    premium: 'Liberada',
  },
];

export function LandingPage() {
  const settings = useSettings();
  const homepage = settings.homepage as {appearance: LandingPageContent};

  const showPricing =
    settings.links.homepage_pricing && settings.billing.enable;

  return (
    <Fragment>
      <DefaultMetaTags />
      <main className="min-h-screen overflow-hidden bg-[#07070A] text-white">
        <HeroHeader content={homepage.appearance} />
        <AdHost slot="landing" className="landing-container mb-40 md:mb-70" />
        <PrimaryFeatures content={homepage.appearance} />
        <WorkflowSection />
        <SecondaryFeatures content={homepage.appearance} />
        <UseCasesSection />
        {settings.links?.homepage_stats && <LandingPageStats />}
        {showPricing && <PricingSection content={homepage.appearance} />}
        <BottomCta content={homepage.appearance} />
        <Footer className="landing-container border-t border-white/10 py-32" />
      </main>
    </Fragment>
  );
}

function HeroHeader({content}: ContentProps) {
  const {hasPermission} = useAuth();
  const {
    links: {homepage_creation},
  } = useSettings();

  const {
    headerTitle,
    headerSubtitle,
    headerImage,
    headerImageOpacity,
    actions,
  } = content;

  return (
    <header className="relative isolate overflow-hidden">
      <img
        data-testid="headerImage"
        src={headerImage}
        style={{opacity: Math.min(Number(headerImageOpacity) || 0.18, 0.18)}}
        alt=""
        width="2347"
        height="1244"
        decoding="async"
        loading="lazy"
        className="absolute left-1/2 top-0 -z-20 min-h-full w-auto min-w-full -translate-x-1/2 object-cover grayscale"
      />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_78%_22%,rgb(243_17_91/0.24),transparent_34rem),linear-gradient(115deg,rgb(7_7_10)_0%,rgb(7_7_10/0.96)_48%,rgb(16_16_24)_100%)]" />
      <div className="h-220 absolute inset-x-0 bottom-0 -z-10 bg-[linear-gradient(180deg,transparent,rgb(7_7_10))]" />

      <Navbar
        color="transparent"
        className="landing-container h-72 border-b border-white/10 bg-transparent px-0"
        menuPosition="homepage-navbar"
        primaryButtonColor="primary"
        alwaysDarkMode
      />

      <div className="landing-container shorty-hero-grid grid min-h-[calc(100svh-72px)] grid-cols-[minmax(0,1fr)] items-center gap-32 pb-64 pt-44 sm:gap-40 lg:min-h-[720px] lg:grid-cols-[minmax(0,1fr)_480px] lg:gap-48 lg:pb-112 lg:pt-76">
        <section className="shorty-hero-copy w-full min-w-0 max-w-720">
          <div className="text-white/72 mb-20 inline-flex max-w-full items-center gap-10 overflow-hidden rounded-full border border-white/10 bg-white/10 px-14 py-8 text-xs font-medium uppercase tracking-widest">
            <span className="h-8 w-8 rounded-full bg-primary shadow-[0_0_22px_rgb(243_17_91/0.9)]" />
            <span className="truncate">
              <Trans message="Links inteligentes para vender, medir e escalar" />
            </span>
          </div>
          {headerTitle && (
            <h1
              className="max-w-680 text-2xl font-semibold leading-tight tracking-normal sm:text-4xl md:text-6xl"
              data-testid="headerTitle"
            >
              <Trans message={headerTitle} />
            </h1>
          )}
          {headerSubtitle && (
            <p
              className="text-white/72 mt-22 max-w-640 text-lg leading-8 md:text-xl"
              data-testid="headerSubtitle"
            >
              <Trans message={headerSubtitle} />
            </p>
          )}

          <div className="shorty-hero-highlights mt-32 flex min-w-0 flex-wrap gap-12">
            {productHighlights.map(item => (
              <span
                key={item}
                className="text-white/72 rounded-full border border-white/10 bg-white/5 px-14 py-8 text-sm"
              >
                <Trans message={item} />
              </span>
            ))}
          </div>

          <div className="mt-36 flex flex-col gap-14 sm:flex-row">
            <CtaButton
              item={actions.cta1}
              variant="raised"
              color="primary"
              size="lg"
              radius="rounded-full"
              data-testid="cta1"
              className="w-full justify-center sm:w-auto sm:min-w-180"
            />
            <CtaButton
              item={actions.cta2}
              variant="outline"
              color="white"
              size="lg"
              radius="rounded-full"
              data-testid="cta2"
              className="w-full justify-center sm:w-auto"
            />
          </div>

          {homepage_creation && hasPermission('links.create') && (
            <div className="mt-40 min-w-0 max-w-680">
              <LandingPageNewLinkForm content={content} />
            </div>
          )}
        </section>

        <ProductPreview />
      </div>
    </header>
  );
}

interface CtaButtonProps extends ButtonProps {
  item?: MenuItemConfig;
}
function CtaButton({item, ...buttonProps}: CtaButtonProps) {
  if (!item?.label || !item?.action) return null;
  const Icon = item.icon ? createSvgIconFromTree(item.icon) : undefined;
  return (
    <Button
      elementType={item.type === 'route' ? Link : 'a'}
      href={item.action}
      to={item.action}
      startIcon={Icon ? <Icon /> : undefined}
      {...buttonProps}
    >
      <Trans message={item.label} />
    </Button>
  );
}

function ProductPreview() {
  return (
    <aside className="shorty-panel hidden overflow-hidden lg:block">
      <div className="flex items-center justify-between border-b border-white/10 px-20 py-16">
        <div>
          <div className="text-sm font-medium text-white">Shorty Analytics</div>
          <div className="text-white/48 text-xs">Campanha Junho</div>
        </div>
        <div className="rounded-full bg-primary/12 px-12 py-6 text-xs font-medium text-primary">
          live
        </div>
      </div>
      <div className="p-20">
        <div className="grid grid-cols-3 gap-12">
          {platformMetrics.map(metric => (
            <div
              key={metric.label}
              className="rounded-lg border border-white/10 bg-white/5 p-14"
            >
              <div className="text-2xl font-semibold text-white">
                {metric.value}
              </div>
              <div className="text-white/52 mt-6 text-xs leading-5">
                <Trans message={metric.label} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-18 rounded-lg border border-white/10 bg-[#101018] p-18">
          <div className="mb-16 flex items-center justify-between text-sm">
            <span className="text-white/72">Cliques por canal</span>
            <span className="text-primary">+32%</span>
          </div>
          {[72, 48, 84, 55, 92, 66, 78].map((height, index) => (
            <span
              key={index}
              className="mr-10 inline-block w-34 rounded-t bg-[linear-gradient(180deg,#F3115B,#8B0E3D)] align-bottom"
              style={{height}}
            />
          ))}
        </div>
        <div className="mt-18 space-y-10">
          {['shorty.com.br/oferta', 'shorty.com.br/bio', 'shorty.com.br/demo'].map(
            (link, index) => (
              <div
                key={link}
                className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-14 py-12 text-sm"
              >
                <span className="text-white/72">{link}</span>
                <span className="text-white/48">{(index + 1) * 1240}</span>
              </div>
            ),
          )}
        </div>
      </div>
    </aside>
  );
}

function PrimaryFeatures({content}: ContentProps) {
  return (
    <section className="landing-container py-70" id="primary-features">
      <SectionHeader
        eyebrow="Recursos principais"
        title="Tudo para transformar Links em canais de conversão."
        description="Crie, organize, proteja e acompanhe cada destino em uma experiência coesa para times e campanhas."
      />
      <div className="mt-36 grid gap-16 md:grid-cols-3">
        {content.primaryFeatures.map((feature, index) => (
          <article
            key={index}
            className="shorty-panel shorty-card-hover p-24"
            data-testid={`primary-root-${index}`}
          >
            <div className="mb-24 flex h-54 w-54 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
              <img
                className="h-32 w-32"
                data-testid={`primary-image-${index}`}
                src={feature.image}
                alt=""
              />
            </div>
            <h2
              className="text-xl font-semibold text-white"
              data-testid={`primary-title-${index}`}
            >
              <Trans message={feature.title} />
            </h2>
            <p
              className="mt-12 text-sm leading-6 text-white/70"
              data-testid={`primary-subtitle-${index}`}
            >
              <Trans message={feature.subtitle} />
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

function WorkflowSection() {
  return (
    <section className="border-y border-white/10 bg-[#101018] py-70">
      <div className="landing-container">
        <SectionHeader
          eyebrow="Como funciona"
          title="Do Link ao relatório em poucos minutos."
          description="Um fluxo direto para criar experiências rastreáveis sem perder controle operacional."
        />
        <div className="mt-36 grid gap-16 md:grid-cols-3">
          {workflowSteps.map((step, index) => (
            <article key={step.title} className="relative rounded-lg p-22">
              <div className="mb-18 flex h-42 w-42 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                {index + 1}
              </div>
              <h3 className="text-lg font-semibold">
                <Trans message={step.title} />
              </h3>
              <p className="mt-10 text-sm leading-6 text-white/70">
                <Trans message={step.description} />
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function SecondaryFeatures({content}: ContentProps) {
  return (
    <section className="landing-container py-80" id="features">
      {content.secondaryFeatures.map((feature, index) => {
        const isEven = index % 2 === 0;
        return (
          <div
            key={index}
            data-testid={`secondary-root-${index}`}
            className="grid items-center gap-34 py-34 md:grid-cols-2 md:py-54"
          >
            <img
              src={feature.image}
              className={clsx(
                'w-full rounded-lg border border-white/10 shadow-[0_24px_80px_rgb(0_0_0/0.34)]',
                isEven && 'md:order-2',
              )}
              data-testid={`secondary-image-${index}`}
              alt=""
            />
            <div className="max-w-500">
              <small
                className="mb-14 block text-xs font-semibold uppercase tracking-widest text-primary"
                data-testid={`secondary-subtitle-${index}`}
              >
                <Trans message={feature.subtitle} />
              </small>
              <h3
                className="text-3xl font-semibold leading-tight text-white"
                data-testid={`secondary-title-${index}`}
              >
                <Trans message={feature.title} />
              </h3>
              <div className="my-20 h-2 w-54 rounded bg-primary" />
              <p
                className="text-base leading-7 text-white/70"
                data-testid={`secondary-description-${index}`}
              >
                <Trans message={feature.description} />
              </p>
            </div>
          </div>
        );
      })}
    </section>
  );
}

function UseCasesSection() {
  return (
    <section className="landing-container pb-80">
      <div className="rounded-lg border border-white/10 bg-[linear-gradient(135deg,rgb(21_21_32),rgb(16_16_24))] p-24 md:p-36">
        <SectionHeader
          eyebrow="Casos de uso"
          title="Pronto para marketing, vendas e operações digitais."
          description="Use o Shorty para padronizar links de campanha, bio, QR codes impressos, links de afiliados e relatórios por time."
        />
        <div className="mt-30 flex flex-wrap gap-12">
          {useCases.map(useCase => (
            <span
              key={useCase}
              className="text-white/72 rounded-full border border-white/10 bg-white/5 px-16 py-10 text-sm"
            >
              <Trans message={useCase} />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

interface PricingSectionProps {
  content: LandingPageContent;
}
function PricingSection({content}: PricingSectionProps) {
  const query = useProducts('landingPage');
  const [selectedCycle, setSelectedCycle] =
    useState<UpsellBillingCycle>('yearly');
  return (
    <section
      className="border-t border-white/10 bg-[#101018] py-80"
      id="pricing"
    >
      <div className="landing-container">
        <SectionHeader
          eyebrow="Planos"
          title={content.pricingTitle}
          description={content.pricingSubtitle}
          align="center"
        />
        <BillingCycleRadio
          products={query.data?.products}
          selectedCycle={selectedCycle}
          onChange={setSelectedCycle}
          className="my-50 flex justify-center"
          size="lg"
        />
        <PricingTable
          selectedCycle={selectedCycle}
          productLoader="landingPage"
          variant="landing"
        />
        <PlanComparison />
      </div>
    </section>
  );
}

function PlanComparison() {
  const gridColumns =
    'grid-cols-[minmax(230px,1.35fr)_repeat(3,minmax(145px,1fr))]';

  return (
    <div className="mt-44 overflow-hidden rounded-panel border border-white/10 bg-white/[0.035]">
      <div className="overflow-x-auto overscroll-x-contain">
        <div className="min-w-[760px]">
          <div
            className={clsx(
              'grid items-center border-b border-white/10 px-20 py-18',
              gridColumns,
            )}
          >
            <div className="min-w-0 pr-20">
              <div className="text-sm font-semibold text-white">
                <Trans message="Comparativo dos planos" />
              </div>
              <div className="mt-4 text-sm leading-6 text-white/58">
                <Trans message="Resumo dos limites comerciais mais importantes." />
              </div>
            </div>
            {['Grátis', 'Básico', 'Premium'].map(plan => (
              <div
                key={plan}
                className="px-12 text-center text-sm font-semibold text-white/80"
              >
                <Trans message={plan} />
              </div>
            ))}
          </div>
          {planComparisonRows.map((row, index) => (
            <div
              key={row.feature}
              className={clsx(
                'grid items-center border-white/10 px-20 py-14 text-sm',
                gridColumns,
                index !== 0 && 'border-t',
              )}
            >
              <div className="min-w-0 pr-20 font-medium leading-6 text-white">
                <Trans message={row.feature} />
              </div>
              <PlanComparisonValue value={row.free} />
              <PlanComparisonValue value={row.basic} />
              <PlanComparisonValue value={row.premium} emphasized />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlanComparisonValue({
  value,
  emphasized,
}: {
  value: string;
  emphasized?: boolean;
}) {
  return (
    <div
      className={clsx(
        'px-12 text-center leading-6 text-white/66',
        emphasized && 'font-semibold text-white',
      )}
    >
      <Trans message={value} />
    </div>
  );
}

function BottomCta({content}: ContentProps) {
  return (
    <section
      className="relative isolate overflow-hidden border-y border-white/10 py-80 text-white md:py-112"
      data-testid="footerImage"
    >
      <img
        src={content.footerImage}
        alt=""
        width="2347"
        height="1244"
        decoding="async"
        loading="lazy"
        className="absolute left-1/2 top-1/2 -z-20 min-h-full w-auto min-w-full -translate-x-1/2 -translate-y-1/2 object-cover opacity-20"
      />
      <div className="absolute inset-0 -z-10 bg-[linear-gradient(135deg,rgb(7_7_10/0.96),rgb(243_17_91/0.22))]" />
      <div className="landing-container text-center">
        <div className="mx-auto max-w-680">
          <h2
            className="text-3xl font-semibold leading-tight sm:text-5xl"
            data-testid="footerTitle"
          >
            <Trans message={content.footerTitle} />
          </h2>
          {content.footerSubtitle && (
            <p
              className="max-w-560 mx-auto mt-18 text-lg leading-8 text-white/70"
              data-testid="footerSubtitle"
            >
              <Trans message={content.footerSubtitle} />
            </p>
          )}
          <CtaButton
            item={content.actions.cta3}
            size="lg"
            radius="rounded-full"
            variant="raised"
            color="primary"
            className="mt-34"
            data-testid="cta3"
          />
        </div>
      </div>
    </section>
  );
}

interface SectionHeaderProps {
  eyebrow: string;
  title: string;
  description: string;
  align?: 'left' | 'center';
}
function SectionHeader({
  eyebrow,
  title,
  description,
  align = 'left',
}: SectionHeaderProps) {
  return (
    <div
      className={clsx(align === 'center' && 'mx-auto max-w-720 text-center')}
    >
      <div className="mb-12 text-xs font-semibold uppercase tracking-widest text-primary">
        <Trans message={eyebrow} />
      </div>
      <h2 className="max-w-760 text-3xl font-semibold leading-tight text-white md:text-4xl">
        <Trans message={title} />
      </h2>
      <p className="mt-14 max-w-680 text-base leading-7 text-white/70">
        <Trans message={description} />
      </p>
    </div>
  );
}
